import React, { useState } from "react";
import logo from "../assets/headerlogo.png";
import { Link, NavLink } from "react-router-dom";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `block rounded-full px-4 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 ${
    isActive
      ? "text-brand-700 bg-brand-50"
      : "text-slate-600 hover:text-brand-700 hover:bg-brand-50/60"
  }`;

export const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const closeMenus = () => {
    setMenuOpen(false);
    setSubmenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/85 backdrop-blur-lg shadow-[0_1px_20px_rgba(15,95,194,0.06)]">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" onClick={closeMenus} className="flex items-center gap-3">
          <img
            src={logo}
            alt="Mind Wonders logo"
            className="h-12 w-auto object-contain transition-transform duration-300 hover:scale-105"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          <NavLink to="/" end className={navLinkClass}>
            HOME
          </NavLink>
          <NavLink to="/about" className={navLinkClass}>
            ABOUT US
          </NavLink>

          <div className="group relative">
            <button
              type="button"
              onClick={() => setSubmenuOpen(!submenuOpen)}
              className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold tracking-wide text-slate-600 transition-colors duration-200 hover:bg-brand-50/60 hover:text-brand-700"
            >
              SERVICES
              <svg
                className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className="invisible absolute left-1/2 top-full w-52 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <ul className="overflow-hidden rounded-2xl border border-slate-100 bg-white p-2 shadow-soft">
                <li>
                  <Link
                    to="/services/individual"
                    onClick={closeMenus}
                    className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
                  >
                    INDIVIDUAL
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/child"
                    onClick={closeMenus}
                    className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
                  >
                    CHILD
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/couple"
                    onClick={closeMenus}
                    className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
                  >
                    COUPLE
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <NavLink to="/contact" className={navLinkClass}>
            CONTACT US
          </NavLink>
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setMenuOpen(!menuOpen)}
          className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-700 transition-colors hover:bg-brand-50 md:hidden"
        >
          {menuOpen ? (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out md:hidden ${
          menuOpen ? "max-h-[28rem] border-t border-slate-100" : "max-h-0"
        }`}
      >
        <nav className="space-y-1 bg-white px-4 pb-6 pt-4">
          <Link
            to="/"
            onClick={closeMenus}
            className="block rounded-xl px-4 py-3 text-sm font-semibold tracking-wide text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            HOME
          </Link>
          <Link
            to="/about"
            onClick={closeMenus}
            className="block rounded-xl px-4 py-3 text-sm font-semibold tracking-wide text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            ABOUT US
          </Link>

          <button
            type="button"
            onClick={() => setSubmenuOpen(!submenuOpen)}
            className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold tracking-wide text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            SERVICES
            <svg
              className={`h-4 w-4 transition-transform duration-200 ${submenuOpen ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ${
              submenuOpen ? "max-h-48" : "max-h-0"
            }`}
          >
            <div className="ml-4 space-y-1 border-l-2 border-brand-100 pl-4">
              <Link
                to="/services/individual"
                onClick={closeMenus}
                className="block rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
              >
                INDIVIDUAL
              </Link>
              <Link
                to="/services/child"
                onClick={closeMenus}
                className="block rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
              >
                CHILD
              </Link>
              <Link
                to="/services/couple"
                onClick={closeMenus}
                className="block rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
              >
                COUPLE
              </Link>
            </div>
          </div>

          <Link
            to="/contact"
            onClick={closeMenus}
            className="block rounded-xl px-4 py-3 text-sm font-semibold tracking-wide text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
          >
            CONTACT US
          </Link>
        </nav>
      </div>
    </header>
  );
};
